import React, { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { Input, Button, Form, Statistic, App, Checkbox, Space, Flex } from "antd";
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaLock, FaUserLock, FaShieldAlt } from "react-icons/fa";
import { Regex, ActionType, API, Enum } from "../resources/constants";
import language from "../resources/languages/fa_IR";

// Interfaces
import { AxiosError } from "../interfaces/axios"

// Contexts
import GlobalContext from "../contexts/global";
import AxiosContext from "../contexts/axios";

// Commons
import Captcha from '../commons/captcha'

const { Countdown } = Statistic
const { Pages: { SignIn: SignInLanguage, SignUp: SignUpLanguage }, Validation, Message: MessageLanguage } = language
const { isNumber, isMobile, isEmail, isMobileOrEmail } = Regex
const { SET_USER, SET_ACCESS_TOKEN } = ActionType
const { USER_MANAGEMENT_AUTH_OTP_CODE, USER_MANAGEMENT_AUTH_SIGN_IN, USER_MANAGEMENT_PROFILE_MY_ACCOUNT, USER_MANAGEMENT_AUTH_SIGN_IN_GOOGLE, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message } = Enum

export default function SignIn() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const [countdown, setCountdown] = useState<boolean>(false)
    const [otp, setOTP] = useState<boolean>(false)
    const [otpSecondsLeft, setOTPSecondsLeft] = useState<number>(0)
    const { meta, baseURL, dispatch } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const navigate = useNavigate()
    const location = useLocation()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
    }, []);

  useEffect(() => {
    setOTPSecondsLeft(0)
    setCountdown(false)
    form.resetFields(['password'])
  }, [otp]);

    const onFinish = ({ username, password, captcha }: { username: string, password: string, captcha: string }) => {
        axios.post(USER_MANAGEMENT_AUTH_SIGN_IN, { username: isMobile.test(username) ? username.substring(1) : username, password, otp, captcha }).then((response: { data: { accessToken: string }}) => {
            const { data: { accessToken } } = response
            Cookies.set('accessToken', accessToken, { expires: 365 })
            dispatch({ type: SET_ACCESS_TOKEN , payload: { accessToken }})
            axios.get(USER_MANAGEMENT_PROFILE_MY_ACCOUNT).then((response: { data: {} }) => {
                const { data: user } = response
                dispatch({ type: SET_USER , payload: { user }})
                navigate(location.state?.from?.pathname || "/", { replace: true });
                message.success(MessageLanguage[Message.SIGN_IN_PROFILE]).then(response => response)
            })
        }, async (error: AxiosError) => {
            const { response: { data } } = error;
            form.resetFields(['captcha'])
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        })
    };

    return (
        <>
          { meta?.title && (
            <Helmet>
              <title>{ SignInLanguage.signIn + ' - ' + meta.title }</title>
            </Helmet>
          )
          }
          <Form form={form} name="sign-in" autoComplete="off" onFinish={onFinish} style={{ maxWidth: 400, margin: 'auto' }}>
            <Space direction="vertical" size="middle" style={{width:'100%'}}>
              {
                meta?.verticalLogo && (
                  <div className={'sign-in-logo'}>
                    <img alt={meta?.title} src={baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + meta?.verticalLogo?.id}
                         width={250} />
                  </div>
                )
              }
              <Button icon={<img src={require('../resources/media/social/google.png')} width={24} />} block
                      onClick={() => window.location.replace(`${baseURL}${USER_MANAGEMENT_AUTH_SIGN_IN_GOOGLE}`)}>
                {SignInLanguage.google}
              </Button>
              <Form.Item
                name="username"
                hasFeedback
                rules={[
                  { required: true, message: `${SignInLanguage.username} ${Validation.required}` },
                  { pattern: isMobileOrEmail, message: Validation.mobileOrEmail },
                ]}
              >
                <Input prefix={<FaUserLock size={15} />} placeholder={SignInLanguage.username} />
              </Form.Item>
              {
                otp ? (
                  <Flex gap="small" justify={'space-between'}>
                    <Form.Item
                      name="password"
                      hasFeedback
                      style={{width:'100%'}}
                      rules={[
                        { required: true, message: `${SignInLanguage.oneTimeCode} ${Validation.required}` },
                        { pattern: isNumber, message: `${SignInLanguage.oneTimeCode} ${Validation.pattern}` },
                        { len: 5, message: `${SignInLanguage.oneTimeCode} 5 ${Validation.length}` },
                      ]}
                    >
                      <Input type="tel" prefix={<FaLock size={15} />} maxLength={5} placeholder={SignInLanguage.oneTimeCode} />
                    </Form.Item>
                    {
                      !countdown ? (
                        <Form.Item shouldUpdate>
                          {() => (
                            <Button type="primary" disabled={!form.getFieldValue('username') || !form.getFieldValue('captcha') || !!form.getFieldsError(['username', 'captcha']).filter(({ errors }) => errors.length).length} ghost block style={{width:'150px'}} onClick={() => {
                              axios.post(USER_MANAGEMENT_AUTH_OTP_CODE, { username: isMobile.test(form.getFieldValue('username')) ? form.getFieldValue('username').substring(1) : form.getFieldValue('username'), captcha: form.getFieldValue('captcha') }).then((response: { data: number }) => {
                                const { data: otpSecondsLeft } = response
                                setOTPSecondsLeft(otpSecondsLeft)
                                form.resetFields(['password', 'captcha'])
                                message.success(MessageLanguage[Message.VERIFY_CODE_SENT]).then(response => response )
                                setCountdown(true)
                              }, (error: AxiosError) => {
                                const { response: { data } } = error;
                                form.resetFields(['password', 'captcha'])
                                // @ts-ignore
                                message.error(MessageLanguage[data.message]).then(response => response )
                              })
                            }}>
                              { SignInLanguage.receiveCode }
                            </Button>
                          )}
                        </Form.Item>
                      ) : (
                        <Form.Item>
                          <Button type="primary" disabled ghost block style={{width:'150px'}}>
                            <Countdown value={Date.now() + Number(otpSecondsLeft) * 1000} format="ss" onFinish={() => {
                              setCountdown(false)
                            }} />
                          </Button>
                        </Form.Item>
                      )
                    }
                  </Flex>
                ) : (
                  <Form.Item
                    name="password"
                    hasFeedback
                    rules={[
                      { required: true, message: `${SignInLanguage.password} ${Validation.required}` },
                    ]}
                  >
                    <Input.Password prefix={<FaLock size={15} />} placeholder={SignInLanguage.password} />
                  </Form.Item>
                )
              }
              <Checkbox checked={!otp} onChange={({ target: { checked} }) => setOTP(!checked)}>{SignInLanguage.hasPassword}</Checkbox>
              <Form.Item
                name="captcha"
                hasFeedback
                rules={[
                  { required: true, message: `${SignInLanguage.captcha} ${Validation.required}` },
                  { pattern: isNumber, message: `${SignInLanguage.captcha} ${Validation.pattern}` },
                  { len: 4, message: `${SignInLanguage.captcha} 4 ${Validation.length}` },
                ]}
              >
                <Input type="tel" prefix={<FaShieldAlt size={15} />} maxLength={4} placeholder={SignInLanguage.captcha} addonAfter={<Captcha />} />
              </Form.Item>
              <Form.Item shouldUpdate>
                {() => (
                  <Button
                    type="primary"
                    block
                    htmlType="submit"
                    disabled={!form.getFieldValue('username') || !form.getFieldValue('password') || !form.getFieldValue('captcha') || !!form.getFieldsError().filter(({ errors }) => errors.length).length}
                  >
                    { SignInLanguage.signIn }
                  </Button>
                )}
              </Form.Item>
              {/*<Button onClick={() => navigate('/sign-up')} block>*/}
              {/*  { SignUpLanguage.signUp }*/}
              {/*</Button>*/}
              {/*<Link to={'/reset-password'}>{SignInLanguage.forgotPassword}</Link>*/}
            </Space>
          </Form>
        </>
    )
}
